// src/modules/app-views/_apps/appmulti/quotations/List/Modals/CompareModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Flex, Modal, Table } from 'antd';
import { CalendarOutlined, CopyOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { FaPlaneArrival, FaPlaneDeparture } from 'react-icons/fa';
import { MdOutlineScreenRotation } from 'react-icons/md';
import dayjs from 'dayjs';
import DarkMultiLogo from 'assets/images/logo-116x70.png';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import appmultiService from 'modules/app-views/_apps/appmulti/services/appmultiService';
import { encodeQuery } from 'utils/urlUtils';
import Icon from 'components/JarvislyComponents/Icon';

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const MultiQuotationCompareModal = props => {
  const { showModal, setShowModal, compareData, compareColumns, openNotification, filter, compare } =
    props;

  // STATE
  const [url, setUrl] = useState();

  // RENDER HELPERS
  const logo = DarkMultiLogo;
  const isMobile = appService.isMobile();

  // EFFECTS
  useEffect(() => {
    if (!showModal) return;
    (async () => {
      const params = await appmultiService.loadParameters();
      setUrl(params?.webconfig?.websiteUrl);
    })();
  }, [showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLERS
  const onClose = () => {
    setShowModal(false);
    openNotification();
  };

  const onClickCopyUrl = () => {
    const qs = {
      destiny: filter?.destiny,
      departure: filter?.departure,
      arrival: filter?.arrival,
      passengers: filter?.passengers,
      compare: compare.map(c => c._id),
    };
    const api = 'travel-insurance-quotations';
    const queryString = encodeQuery(qs);
    const fullUrl = `${url}/${api}?${queryString}`;
    appService.copyToClipboard(fullUrl);
  };

  // RENDER
  return (
    <Modal
      cancelButtonProps={{ style: { display: 'none' } }}
      okText="Fechar"
      width="95vw"
      okButtonProps={{ type: 'default' }}
      title={<div className="text-muted text-uppercase mb-4">{translateX('compare_plans')}</div>}
      open={showModal}
      onCancel={onClose}
      onOk={onClose}
    >
      <img src={logo} width={140} alt="Multi Seguro Viagem." />

      {isMobile && (
        <div className="container-mobile d-flex justify-content-end">
          <Alert
            title={translateX('rotate_cellphone')}
            type="warning"
            showIcon
            icon={<MdOutlineScreenRotation size={24} />}
          />
        </div>
      )}

      <Descriptions bordered items={buildDescriptionsTravelItems(filter)} className="w-100 mb-4" />

      <Table rowKey="key" columns={compareColumns} dataSource={compareData} pagination={false} scroll={{ x: 'max-content' }} />

      <Flex className="mt-3">
        <Button type="dashed" icon={<CopyOutlined />} danger onClick={onClickCopyUrl} />
        <p className="ml-2 mb-0 d-flex align-items-center">{translateX('copy_comparison_url')}</p>
      </Flex>
    </Modal>
  );

  function buildDescriptionsTravelItems(record) {
    if (!record) return [];
    const { passengers = [] } = record;
    return [
      {
        key: 'destiny',
        label: translateX('destiny'),
        span: isMobile ? 3 : 1,
        children: (
          <>
            <div>
              <Icon type={EnvironmentOutlined} />
              <span className="ml-2">{translateX(record?.destiny)}</span>
            </div>
            <div>
              <Icon type={CalendarOutlined} />
              <span className="ml-2">
                {record?.days}{' '}
                <span className="text-lowercase">{translateX(record?.days > 1 ? 'days' : 'day')}</span>
              </span>
            </div>
          </>
        ),
      },
      {
        key: 'period',
        label: translateX('period'),
        span: isMobile ? 3 : 1,
        children: (
          <>
            <Flex align="center">
              <Icon type={FaPlaneDeparture} className="font-size-md" />
              <span className="ml-2 font-weight-semibold">
                {dayjs(record.departure).format('L')}
              </span>
            </Flex>
            <Flex align="center">
              <Icon type={FaPlaneArrival} className="font-size-md" />
              <span className="ml-2 font-weight-semibold">
                {dayjs(record.arrival).format('L')}
              </span>
            </Flex>
          </>
        ),
      },
      {
        key: 'travellers',
        label: translateX('travelers'),
        span: isMobile ? 3 : 1,
        children: (
          <Flex align="center">
            <span className="ml-2 font-weight-semibold">
              {passengers?.length} passageiros
            </span>
          </Flex>
        ),
      },
    ];
  }
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MultiQuotationCompareModal;
